import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../shared/services/api.service';
import {
  GetChartsQuery,
  GetChartsQueryVariables,
  DeleteChartOrDashboardInput,
  EntityType,
  DeleteChartOrDashboardMutation,
  DeleteChartOrDashboardMutationVariables,
  DeleteResponse,
} from '../../../../API';
import { GetCharts, GetDashboardNames } from '../../../../graphql/queries';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { ChartMetadataExtended } from '../../models/charts';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { BuildChartComponent } from '../build-chart/build-chart.component';
import { DeleteChartOrDashboard } from '../../../../graphql/mutations';
import { ChartDataService } from '../../services/chart-data.service';
import { GetDashboardNamesQuery, GetDashboardNamesQueryVariables, DashboardNameInfo } from '../../../../API';
import { compareDate, compareText } from '../../../shared/utils/sort.utils';

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrl: './charts.component.scss',
})
export class ChartsComponent implements OnInit {
  selectedCategory = 'charts';

  headers: any[] = [];
  charts: ChartMetadataExtended[] = [];
  isViewModalVisible = false;
  isViewModalLoading = false;
  chartsToDisplay: ChartMetadataExtended[] = [];
  isLoading = false;

  trackByHeader(index: number, item: { name: string }): string {
    return item.name;
  }

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
    private chartDataService: ChartDataService,
  ) {
    this.setHeaders();
  }

  ngOnInit(): void {
    this.getAllCharts();
  }
  setHeaders(): void {
    this.headers = [
      {
        name: 'Chart Name',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (first: ChartMetadataExtended, second: ChartMetadataExtended) => compareText(first.name, second.name),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Chart Description',
        width: '13.75rem',
        sortOrder: null,
        sortFn: (first: ChartMetadataExtended, second: ChartMetadataExtended) =>
          compareText(first.description, second.description),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Chart Type',
        width: '12.5rem',
        sortOrder: null,
        sortFn: (first: ChartMetadataExtended, second: ChartMetadataExtended) =>
          compareText(first.chartType, second.chartType),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Created On',
        width: '12.5rem',
        sortOrder: 'descend',
        sortFn: (first: ChartMetadataExtended, second: ChartMetadataExtended) =>
          compareDate(first.lastModifiedAt, second.lastModifiedAt),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Created By',
        width: '12.5rem',
        sortOrder: null,
        sortFn: (first: ChartMetadataExtended, second: ChartMetadataExtended) =>
          compareText(first.lastModifiedBy, second.lastModifiedBy),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Action',
        width: '7rem',
        right: '0px',
        sortOrder: null,
        sortFn: null,
        sortDirections: [],
      },
    ];
  }

  getAllCharts(): void {
    this.isLoading = true;
    const orgId = this.userManagementService.getOrgId();
    const appConfig = '';
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        orgId,
      })
      .then((response) => {
        const result: ChartMetadataExtended[] = (response?.data?.GetCharts || []).map((chartItem) => {
          const newItem = {
            ...chartItem,
            chartDisplayImage: `${appConfig}/images/chart/${orgId}/${chartItem?.chartId}/chart.svg`,
          };
          return newItem;
        }) as ChartMetadataExtended[];

        this.chartsToDisplay = this.charts = result;
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  openCreateChartModal(): void {
    this.modalService.create({
      nzTitle: 'Create Chart',
      nzFooter: null,
      nzContent: BuildChartComponent,
      nzMaskClosable: false,
      nzWidth: '90vw',
      nzOnOk: () => {
        this.getAllCharts();
      },
    });
  }
  viewChart(chartId: string): void {
    this.chartDataService.isViewMode = true;
    this.modalService.create({
      nzTitle: 'View Chart',
      nzFooter: null,
      nzContent: BuildChartComponent,
      nzMaskClosable: false,
      nzData: { chartId: chartId },
      nzWidth: '90vw',
      nzOnOk: () => {
        this.getAllCharts();
        this.chartDataService.isViewMode = false;
      },
      nzOnCancel: () => {
        this.chartDataService.isViewMode = false;
      },
    });
  }
  editChart(chartId: string): void {
    this.modalService.create({
      nzTitle: 'Edit Chart',
      nzFooter: null,
      nzContent: BuildChartComponent,
      nzMaskClosable: false,
      nzData: { chartId: chartId },
      nzWidth: '90vw',
      nzOnOk: () => {
        this.getAllCharts();
      },
    });
  }

  async deleteChart(chart: ChartMetadataExtended): Promise<void> {
    if (chart.dashboardIds && chart.dashboardIds.length > 0) {
      try {
        const response = await this.apiService.query<GetDashboardNamesQuery, GetDashboardNamesQueryVariables>(
          GetDashboardNames,
          {
            dashboardIds: chart.dashboardIds as string[],
            orgId: chart.orgId,
          },
        );
        const dashboardNames = response.data?.GetDashboardNames as DashboardNameInfo[];
        const dashboardCount = chart.dashboardIds?.length;

        this.modalService.confirm({
          nzTitle: `Cannot Delete Chart`,
          nzContent: `This chart is currently associated with ${dashboardCount} dashboard(s) and cannot be deleted. To proceed with deletion, please remove the chart from all associated dashboards.<br><br><strong>Associated Dashboards:</strong><br>• ${
            dashboardNames
              ?.map((d) => d.name)
              .filter((name) => name)
              .join('<br>• ') || '--'
          }`,
          nzOkText: 'Ok',
          nzCancelText: null,
        });
        return;
      } catch (error) {
        console.error('Error fetching dashboard names:', error);
        this.modalService.confirm({
          nzTitle: `Cannot Delete Chart`,
          nzContent: `This chart is currently associated with ${chart.dashboardIds.length} dashboard(s) and cannot be deleted. To proceed with deletion, please remove the chart from all associated dashboards.`,
          nzOkText: 'Ok',
          nzCancelText: null,
        });
        return;
      }
    }

    this.modalService.confirm({
      nzTitle: `Are you sure you want to delete this chart?`,
      nzContent: 'Deleting this chart will permanently remove it from your workspace. This action cannot be undone.',
      nzOkText: 'Yes, Delete',
      nzOnOk: () => this.delete(chart),
      nzCancelText: 'Cancel',
    });
  }

  private delete(chart: ChartMetadataExtended) {
    console.log(chart);
    this.isLoading = true;
    const deleteInput: DeleteChartOrDashboardInput = {
      orgId: chart.orgId,
      entityId: chart.chartId,
      entityType: EntityType.CHART,
    };
    this.apiService
      .query<DeleteChartOrDashboardMutation, DeleteChartOrDashboardMutationVariables>(DeleteChartOrDashboard, {
        input: deleteInput,
      })
      .then((response) => {
        const deleteResponse = response.data?.DeleteChartOrDashboard as DeleteResponse;
        if (deleteResponse.statusCode !== 200) {
          this.nzMessageService.error(`Failed to delete chart: ${deleteResponse.message}`);
        } else {
          this.nzMessageService.success(`Chart deleted successfully`);
          this.getAllCharts();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to delete chart: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
